import styled, { css } from 'styled-components';
import { stylePropertiesToCss } from '../../../design-tokens/style-mixins';
import { pxToRem } from '../../../design-tokens/style-utilities';
import { BASE_CLASSNAME } from '../enum';
import { toDisplayName } from './toDisplayName';
import { variants } from './variants';
/**
 * Creates a new Styled Component based on the name and the current theme.
 */
export const createVariant = ({ name, tagName, baseStyle: baseStyleSelector, responsiveStyles: responsiveStyleSelectors, }) => {
    if (variants.has(name)) {
        /**
         * We should discourage overwriting variants.
         */
        throw new Error(`Typography variant "${name}" already exists.`);
    }
    /**
     * Creates the base styling for the Styled Component. It passes the `props` which
     * may contain a `theme` from the ThemeProvider.
     */
    const baseStyle = css((props) => {
        const defaultBaseStyleProps = {
            fontStyle: 'Normal',
        };
        const styleProperties = Object.assign(Object.assign({}, defaultBaseStyleProps), baseStyleSelector(props));
        return stylePropertiesToCss(styleProperties);
    });
    /**
     * Creates the responsive styles for each selector passed. It works similarly to the
     * baseStyle, except that it is wrapped inside a media query.
     */
    const responsiveStyles = responsiveStyleSelectors &&
        css((props) => responsiveStyleSelectors(props).map(([minWidth, styleObject]) => css `
					@media (min-width: ${pxToRem(minWidth)}) {
						${stylePropertiesToCss(styleObject)}
					}
				`));
    /**
     * The complete styling for this variant.
     */
    const styles = css `
		${baseStyle}
		${responsiveStyles}
	`;
    /**
     * This is where the actual Styled Component is created.
     */
    const TypographyVariantComponent = styled(tagName).attrs({
        className: `${BASE_CLASSNAME} ${BASE_CLASSNAME}--${name}`,
    }) `
		${styles}
	`;
    /**
     * In order to satisfy React, we should give it a displayName.
     */
    TypographyVariantComponent.displayName = `${toDisplayName(name)}Typography`;
    /**
     * We also store the styles as a separate object in our configuration object,
     * so that these can be used in legacy projects that do not support Styled
     * Components, but are able to inject a global style using `createGlobalStyle`.
     */
    const configuration = { styles, component: TypographyVariantComponent };
    /**
     * Store the newly created component in our variants map so we can retrieve it is
     * being referenced to from the Typography component.
     */
    variants.set(name, configuration);
    return TypographyVariantComponent;
};
